
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type NumberBase = 'bin' | 'oct' | 'dec' | 'hex';
type WordSize = 8 | 16 | 32 | 64;

const ProgrammingCalculator = () => {
  const [activeBase, setActiveBase] = useState<NumberBase>('dec');
  const [binaryValue, setBinaryValue] = useState<string>('0');
  const [octalValue, setOctalValue] = useState<string>('0');
  const [decimalValue, setDecimalValue] = useState<string>('0');
  const [hexValue, setHexValue] = useState<string>('0');
  const [wordSize, setWordSize] = useState<WordSize>(32);
  const [operandA, setOperandA] = useState<string>('0');
  const [operandB, setOperandB] = useState<string>('0');
  const [operationResult, setOperationResult] = useState<string>('');
  const [selectedOperation, setSelectedOperation] = useState<string>('AND');

  const validateBinary = (value: string): boolean => {
    return /^[01]*$/.test(value);
  };

  const validateOctal = (value: string): boolean => {
    return /^[0-7]*$/.test(value);
  };

  const validateDecimal = (value: string): boolean => {
    return /^[0-9]*$/.test(value);
  };

  const validateHex = (value: string): boolean => {
    return /^[0-9A-Fa-f]*$/.test(value);
  };

  const updateAllValues = (value: string, base: NumberBase) => {
    if (value === '') value = '0';
    
    try {
      let decimalNumber: number;
      
      switch (base) {
        case 'bin':
          if (!validateBinary(value)) return;
          decimalNumber = parseInt(value, 2);
          break;
        case 'oct':
          if (!validateOctal(value)) return;
          decimalNumber = parseInt(value, 8);
          break;
        case 'dec':
          if (!validateDecimal(value)) return;
          decimalNumber = parseInt(value, 10);
          break;
        case 'hex':
          if (!validateHex(value)) return;
          decimalNumber = parseInt(value, 16);
          break;
      }

      // Apply word size limit
      decimalNumber = applyWordSize(decimalNumber, wordSize);
      
      setBinaryValue(decimalNumber.toString(2));
      setOctalValue(decimalNumber.toString(8));
      setDecimalValue(decimalNumber.toString(10));
      setHexValue(decimalNumber.toString(16).toUpperCase());
    } catch (e) {
      // Handle invalid input
      console.error("Invalid conversion:", e);
    }
  };

  // Apply word size limit to the number
  const applyWordSize = (value: number, size: WordSize): number => {
    const maxValue = Math.pow(2, size) - 1;
    return value & maxValue;
  };

  const handleInputChange = (value: string, base: NumberBase) => {
    switch (base) {
      case 'bin':
        setBinaryValue(value);
        break;
      case 'oct':
        setOctalValue(value);
        break;
      case 'dec':
        setDecimalValue(value);
        break;
      case 'hex':
        setHexValue(value.toUpperCase());
        break;
    }
    updateAllValues(value, base);
  };

  const performBitwiseOperation = () => {
    const a = parseInt(operandA, 10);
    const b = parseInt(operandB, 10);
    let result: number;
    
    switch (selectedOperation) {
      case 'AND':
        result = a & b;
        break;
      case 'OR':
        result = a | b;
        break;
      case 'XOR':
        result = a ^ b;
        break;
      case 'NOT':
        result = ~a & ((1 << wordSize) - 1); // Apply word size mask
        break;
      case 'SHL': // Shift left
        result = (a << b) & ((1 << wordSize) - 1);
        break;
      case 'SHR': // Shift right
        result = a >>> b;
        break;
      default:
        result = 0;
    }
    
    setOperationResult(result.toString(10));
  };

  // Update values when word size changes
  useEffect(() => {
    updateAllValues(decimalValue, 'dec');
  }, [wordSize]);

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <Tabs defaultValue="converter" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="converter">Number Converter</TabsTrigger>
              <TabsTrigger value="bitwise">Bitwise Operations</TabsTrigger>
            </TabsList>
            
            <TabsContent value="converter" className="space-y-4">
              <div className="flex flex-col space-y-1">
                <Label htmlFor="wordSize">Word Size</Label>
                <Select 
                  value={wordSize.toString()} 
                  onValueChange={(value) => setWordSize(parseInt(value) as WordSize)}
                >
                  <SelectTrigger id="wordSize">
                    <SelectValue placeholder="Word size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="8">8-bit</SelectItem>
                    <SelectItem value="16">16-bit</SelectItem>
                    <SelectItem value="32">32-bit</SelectItem>
                    <SelectItem value="64">64-bit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="binary">Binary (Base 2)</Label>
                  <Input
                    id="binary"
                    value={binaryValue}
                    onChange={(e) => handleInputChange(e.target.value, 'bin')}
                    className={activeBase === 'bin' ? 'border-primary' : ''}
                    onFocus={() => setActiveBase('bin')}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="octal">Octal (Base 8)</Label>
                  <Input
                    id="octal"
                    value={octalValue}
                    onChange={(e) => handleInputChange(e.target.value, 'oct')}
                    className={activeBase === 'oct' ? 'border-primary' : ''}
                    onFocus={() => setActiveBase('oct')}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="decimal">Decimal (Base 10)</Label>
                  <Input
                    id="decimal"
                    value={decimalValue}
                    onChange={(e) => handleInputChange(e.target.value, 'dec')}
                    className={activeBase === 'dec' ? 'border-primary' : ''}
                    onFocus={() => setActiveBase('dec')}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="hexadecimal">Hexadecimal (Base 16)</Label>
                  <Input
                    id="hexadecimal"
                    value={hexValue}
                    onChange={(e) => handleInputChange(e.target.value, 'hex')}
                    className={activeBase === 'hex' ? 'border-primary' : ''}
                    onFocus={() => setActiveBase('hex')}
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="bitwise" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="operation">Operation</Label>
                <Select value={selectedOperation} onValueChange={setSelectedOperation}>
                  <SelectTrigger id="operation">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">AND</SelectItem>
                    <SelectItem value="OR">OR</SelectItem>
                    <SelectItem value="XOR">XOR</SelectItem>
                    <SelectItem value="NOT">NOT</SelectItem>
                    <SelectItem value="SHL">Shift Left</SelectItem>
                    <SelectItem value="SHR">Shift Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="operandA">Operand A</Label>
                <Input
                  id="operandA"
                  type="text"
                  value={operandA}
                  onChange={(e) => setOperandA(e.target.value)}
                />
              </div>
              
              {selectedOperation !== 'NOT' && (
                <div className="space-y-2">
                  <Label htmlFor="operandB">Operand B</Label>
                  <Input
                    id="operandB"
                    type="text"
                    value={operandB}
                    onChange={(e) => setOperandB(e.target.value)}
                  />
                </div>
              )}
              
              <Button onClick={performBitwiseOperation} className="w-full">
                Calculate
              </Button>
              
              {operationResult && (
                <div className="mt-4 p-4 border rounded-md bg-slate-50">
                  <h3 className="font-semibold">Result:</h3>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-xs text-gray-500">Decimal:</p>
                      <p className="font-mono">{operationResult}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Hexadecimal:</p>
                      <p className="font-mono">{parseInt(operationResult).toString(16).toUpperCase()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Binary:</p>
                      <p className="font-mono text-xs break-all">{parseInt(operationResult).toString(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Octal:</p>
                      <p className="font-mono">{parseInt(operationResult).toString(8)}</p>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProgrammingCalculator;
